import { useApi } from './Api';
import React, { useState, useEffect, useRef } from 'react';
import { useCart } from "react-use-cart";
import { Link } from 'react-router-dom';
import Ariane from './Ariane';
import BottomNavigation from './BottomNavigation';
import Repeatable from 'react-repeatable'; 
import DataTable from 'react-data-table-component';
import bs5Styles from '../assets/themes/bs5';
import notfound from '../assets/images/notfound.png';
import { strings } from '../localization';
import { useIndexedDB } from 'react-indexed-db';

const getClientGeneralDiscount = () => {
    let clientDiscount = 0;
    if (sessionStorage.getItem('orders.current_client')) {
        const selectedClient = JSON.parse(sessionStorage.getItem('orders.current_client'));
        if (selectedClient.general_discount && selectedClient.general_discount > 0) {
            clientDiscount = selectedClient.general_discount;
        }
    }
    return parseFloat(clientDiscount);
}

const getDiscountPrice = (regular_price, discount) => {
    let generalDiscount = getClientGeneralDiscount();
    if (generalDiscount > 0 && (discount === undefined || discount === null)) {
        regular_price = parseFloat(regular_price);
        return Math.round((regular_price - (regular_price * generalDiscount/100))*100)/100;
    }
    return Math.round((regular_price - (regular_price * discount/100))*100)/100 
}
const isDiscounted= (qty, discount) => {
    let generalDiscount = getClientGeneralDiscount();
    if (generalDiscount > 0) {
        return true;
    }
    return discount && qty >= discount.min
}
const getProductPackUnit = (id, defaultVal) => {
    const storedPackUnit = sessionStorage.getItem('products.productPackUnit.' + id);
    return normalizePositiveInteger(storedPackUnit, defaultVal);
}

const normalizePositiveInteger = (value, fallback = 1) => {
    const parsedValue = parseInt(value, 10);
    const parsedFallback = parseInt(fallback, 10);

    if (Number.isFinite(parsedValue) && parsedValue > 0) {
        return parsedValue;
    }

    return Number.isFinite(parsedFallback) && parsedFallback > 0 ? parsedFallback : 1;
}

const normalizeNonNegativeInteger = (value, fallback = 0) => {
    const parsedValue = parseInt(value, 10);
    const parsedFallback = parseInt(fallback, 10);

    if (Number.isFinite(parsedValue) && parsedValue >= 0) {
        return parsedValue;
    }

    return Number.isFinite(parsedFallback) && parsedFallback >= 0 ? parsedFallback : 0;
}

const isPromoDiscounted = (product, allProducts) => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied && typeof product.discount === 'number') {
        if (product.freeItem) {
            return true;
        }
        return getFinalDiscountPercentage(product, 1, allProducts) > 0;
    }
    return !product.destock && isDiscounted(product.quantity, product?.discount);
}

const getPromoDiscountPrice = (regular_price, discount) => {
    regular_price = parseFloat(regular_price);
    return Math.round((regular_price - (regular_price * parseFloat(discount)/100))*100)/100;
}

const recursiveFindProductCategory = (cats, ref) => {
    if (cats.ref == ref)
        return cats;

    const found = cats.parent ? recursiveFindProductCategory(cats.parent, ref) : false;
    return found;
}

const getAppliedDiscountType = () => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied) {
        const promoType = promoCodeApplied.type;
        return promoType;
    }
    return 0;
}

const getFinalDiscountPercentage = (row, qty, allProducts = [], cartItem = true) => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied) {
        const promoType = promoCodeApplied.type;
        let promoPercentage = promoCodeApplied.percentage ?? 0;
        
        let productData = row;
        if (cartItem) {
            productData = allProducts?.find(p => p.ref === row.id);
        }
        let productRef = productData?.ref ?? productData?.id;

        switch (promoType) {
            case 1:
                // TYPE_TOTAL_CART
                return promoPercentage;
            case 2:
                // TYPE_UNIVERSE_COLLECTION
                return (productData !== undefined && recursiveFindProductCategory(productData.categories, promoCodeApplied.categoryId)) ? promoPercentage : 0;
            case 3:
                // TYPE_PRODUCT_ID
                return (productData !== undefined && productRef === promoCodeApplied.productId) ? promoPercentage : 0;
            case 4:
                // TYPE_PRODUCT_TYPE
                return (productData !== undefined && productData.utilisation === promoCodeApplied.productType) ? promoPercentage : 0;
            default:
                return 0;
        }
    }

    if (!row.destock && isDiscounted(qty, row?.discount)) {
        let generalDiscount = getClientGeneralDiscount();
        if (generalDiscount > 0 && (row?.discount?.amount === undefined || row?.discount?.amount === null)) {
            return generalDiscount;
        }
        let defaultDiscount = parseInt(row?.discount?.amount);
        return isNaN(defaultDiscount) ? 0 : defaultDiscount;
    }
    return 0;
}

const getFinalDiscountPrice = (row, qty, allProducts = [], cartItem = true) => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied) {
        let promoPercentage = getFinalDiscountPercentage(row, qty, allProducts, cartItem);

        return getPromoDiscountPrice(row.regular_price, promoPercentage);
    }

    if (!row.destock && isDiscounted(qty, row?.discount)) {
        return getDiscountPrice(row.regular_price, row?.discount?.amount);
    }
    return row.regular_price;
}

const getFreeProductMinQty = () => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied) {
        return promoCodeApplied.minQuantity;
    }
    return 0;
}

const getFreeProductBaseData = (row, qty, allProducts = [], cartItem = true) => {
    const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
    if (promoCodeApplied) {
        const promoType = promoCodeApplied.type;
        const promoMinQty = promoCodeApplied.minQuantity ?? 2;

        if (promoType != 5) {
            return null;
        }
        
        let productData = row;
        if (cartItem) {
            productData = allProducts?.find(p => p.ref === row.id);
        }
        let productRef = productData?.ref ?? productData?.id;

        if (productData !== undefined && productRef === promoCodeApplied.productId) {
            const prodQty = qty / productData.pack_unit;
            if (prodQty < promoMinQty) {
                return null;
            }

            return productData;
        }
    }

    return null;
}


export default function Cart() {
    const {
      isEmpty
    } = useCart();
  
    
  
    return (        
        <div className="products h-100 d-flex flex-column justify-content-between">
            <Ariane>
            <h1>{ strings.details_commande }</h1>
            </Ariane>
            <div className='container-xxl flex-grow-1 '>
                { isEmpty ? 
                <div className="alert alert-dark"><i className="fa fa-shopping-bag me-3"></i>{ strings.panier_vide }</div>
                :
                <CartItems noTableHead={false} />}
            </div>
            <BottomNavigation className="justify-content-end">
                
                <div className='col-4'>
                    <MiniCart />
                </div>
            </BottomNavigation>
        </div>
    );

}

function formatCurrency(number, locale = 'fr-FR'){
    
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(number)
}

function CartItems(props){
    const [ user, { fetchAPI } ] = useApi();
    const { getAll, update } = useIndexedDB('catalog');

    const {
        items,
        updateItem,
        removeItem,
        cartTotal, 
        getItem,
        addItem,
    } = useCart();   
    
    const paginationComponentOptions = {
        rowsPerPageText: strings.articles_page,
        rangeSeparatorText:  strings.articles_of,
        selectAllRowsItem: true,
        selectAllRowsItemText: strings.articles_all,        
        
    };

    const [orderPageUrl, setOrderPageUrl] = useState('/client/order');
    const [allProducts, setAllProducts] = useState([]);
    useEffect(() => {
        if (sessionStorage.getItem('orders.current_client')) {
            setOrderPageUrl('/orders/new');
        }

        if (sessionStorage.getItem('orders.applied_promo')) {
            const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
            setAppliedPromoCode(promoCodeApplied.code);
        }

        getAll().then(dataFromDB => {
            setAllProducts(dataFromDB.find(i => i.type == 'products')?.data ?? []);
        });
    }, []);

    const [promoChecking, setPromoChecking] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const handlePromoInput = (e) => {
        setPromoCode(e.target.value)
    }

    const [appliedPromoCode, setAppliedPromoCode] = useState('');
    const [invalidPromoCode, setInvalidPromoCode] = useState(false);
    const [nonEligibleProductsPromo, setNonEligibleProductsPromo] = useState(false);
    const checkPromoCode = () => {
        if (!promoCode) {
            return;
        }
        setInvalidPromoCode(false);
        setPromoChecking(true);
        setNonEligibleProductsPromo(false);

        // check promo call
        fetchAPI('/promo?code=' + promoCode).then( response => {
            if (response?.data) {
                applyPromoDiscount(response.data[0]);
            } else {
                setInvalidPromoCode(true);
                applyPromoDiscount(null);
            }
            setPromoChecking(false);
        });
    }

    const applyPromoDiscount = (promoInfo, inputRow = null, inputQty = null) => {
        if (promoInfo === null) {
            setAppliedPromoCode('');
            sessionStorage.removeItem('orders.applied_promo');
        } else {
            setAppliedPromoCode(promoInfo.code);
            sessionStorage.setItem('orders.applied_promo', JSON.stringify(promoInfo));
        }

        // reset all cart items with original prices
        for (const key in items) {
            if (Object.prototype.hasOwnProperty.call(items, key)) {
                const cartItem = items[key];
                updateItem(cartItem.id, {
                    price: cartItem.regular_price,
                    regular_price: cartItem.regular_price,
                    discount: 0
                })
            }
        }

        let clearFreeItems = false;
        let discountItemsCount = 0;
        const discountType = getAppliedDiscountType();
        if (discountType > 0) {
            for (const key in items) {
                if (Object.prototype.hasOwnProperty.call(items, key)) {
                    const product = items[key];
                    let currentProdQty = product.quantity;
                    if(inputRow !== null && inputQty !== null) {
                        const inputRowId = inputRow?.ref ?? inputRow?.id;
                        if (inputRowId == product.id) {
                            currentProdQty = inputQty;
                        }
                    }

                    // ignore free item
                    if (product.freeItem) {
                        continue;
                    }

                    // TYPE_BUYX_GET1_FREE
                    if (discountType == 5) {
                        const freeProduct = getFreeProductBaseData(product, currentProdQty, allProducts);
                        const freeItemRef = 'F' + product.id;
                        // skip if not a valid discount product
                        if (freeProduct == null) {
                            // remove free item already added
                            const toRemoveFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);
                            if (toRemoveFreeItem !== undefined) {
                                removeItem(toRemoveFreeItem.id);
                            }
                            continue;
                        }
                        discountItemsCount++;

                        const prodPackUnit = getProductPackUnit(product.ref, product.pack_unit);
                        const prodPackQty = currentProdQty / prodPackUnit;

                        const existingFreeItem = items.find((i) => i.id === freeItemRef && i.freeItem == true);

                        let promoMinQty = getFreeProductMinQty();
                        let freeQtyConst = Math.floor(prodPackQty / promoMinQty);
                        let freeItemQty = prodPackUnit * freeQtyConst;

                        if (existingFreeItem !== undefined) {
                            updateItem(getItem(freeItemRef).id, {
                                quantity: freeItemQty,
                                price: 0,
                                regular_price: freeProduct.regular_price,
                                discount: 0
                            })
                        } else {
                            addItem({
                                id: freeItemRef,
                                price: 0,
                                name: freeProduct.title,
                                image: freeProduct.images.length ? freeProduct.images : null,
                                pack_unit: prodPackUnit,
                                regular_price: freeProduct.regular_price,
                                discount: 0,
                                destock: freeProduct.destock,
                                freeItem: true,
                            }, freeItemQty);
                        }
                    } else {
                        clearFreeItems = true;
                        const discountPrice = getFinalDiscountPrice(product, currentProdQty, allProducts);
                        const discountPercentage = getFinalDiscountPercentage(product, currentProdQty, allProducts);

                        if (discountPercentage > 0) {
                            discountItemsCount++;
                        }
                        updateItem(
                            product.id, {
                                quantity: currentProdQty,
                                price: discountPrice,
                                regular_price: product.regular_price,
                                discount: discountPercentage
                            }
                        );
                    }
                }
            }
        } else {
            clearFreeItems = true;
        }

        if (clearFreeItems) {
            // check and remove any free item if not BXG1 promo
            const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
            for (const k in toRemoveFreeItems) {
                if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                const itemToRemove = toRemoveFreeItems[k];

                removeItem(itemToRemove.id);
            }
        }
    
        if (promoInfo !== null && discountItemsCount === 0) {
            setNonEligibleProductsPromo(true);
            applyPromoDiscount(null);
        }
    }
    
    const canApplyPromo = true;

    const handleRemoveItemWithDiscount = (row) => {
        removeFreeItemAndCode(row, null);

        // default remove item behavouir
        removeItem(row.id);
    }

    const handleUpdateItemWithDiscount = (row, qty) => {
        // default update item behavouir
        updateItem(
            row.id,
            {
                quantity : qty,
                price : getFinalDiscountPrice(row, qty, allProducts),
                discount: getFinalDiscountPercentage(row, qty, allProducts)
            }
        );

        // update free-item
        removeFreeItemAndCode(row, qty);
    }

    const removeFreeItemAndCode = (row, qty = null) => {
        // check if base discount product is still in cart, if not remove free-item
        const promoCodeApplied = JSON.parse(sessionStorage.getItem('orders.applied_promo'));
        if (promoCodeApplied) {
            let clearFreeItems = false;
            let removeAppliedPromo = false;

            const discountType = getAppliedDiscountType();
            // check if free-item discount code
            if (discountType == 5) {
                // check if base discount-product is being removed
                if (row.id === promoCodeApplied.productId) {
                    if (qty !== null) {
                        const promoMinQty = promoCodeApplied.minQuantity ?? 2;
                        const prodQty = qty / row.pack_unit;
                        if (prodQty < promoMinQty) {
                            clearFreeItems = true;
                            removeAppliedPromo = true;
                        } else {
                            applyPromoDiscount(promoCodeApplied, row, qty);
                        }
                    } else {
                        clearFreeItems = true;
                        removeAppliedPromo = true;
                    }
                }
            }

            if (clearFreeItems) {
                // check and remove any free item if invalid promo
                const toRemoveFreeItems = items.filter((i) => i.freeItem === true);
                for (const k in toRemoveFreeItems) {
                    if (!Object.hasOwn(toRemoveFreeItems, k)) continue;
                    const itemToRemove = toRemoveFreeItems[k];

                    removeItem(itemToRemove.id);
                    removeAppliedPromo = true;
                }
            }

            if (removeAppliedPromo) {
                setAppliedPromoCode('');
                setNonEligibleProductsPromo(true);
                sessionStorage.removeItem('orders.applied_promo');
            }
        }
        if (typeof props.onUpdate === "function") {
            props.onUpdate(true);
        }
    }

    return (
        <>
        <DataTable
            columns={[ 
                { 
                    name: <i className='fa fa-image'></i>,
                    selector: row => row.image, 
                    format: row => <Link to={ "/product/"+row.id.replace('F', '') }>{ row.image ? <img src={ row.image } alt="" className="img-fluid img-cover" /> : <img src={ notfound } alt="" className="img-fluid img-cover" />}</Link>, 
                    compact: true, 
                    center: true, 
                    width:'50px',
                },
                { name: strings.cart_table_title, selector: row => row.name },
                { name: strings.cart_table_qty, selector: row => row.quantity, format: row => row.freeItem ? row.quantity : <QuantitySelector locked="true" layout="horizontal" updateQuantity={ (qty) => handleUpdateItemWithDiscount(row, qty) } packUnit={ getProductPackUnit(row.id, row.pack_unit) } quantity={ row.quantity } />, center: true, width: '200px'},
                { name: strings.cart_table_price, selector: row => row.price, format: row => isPromoDiscounted(row, allProducts) ? <><s className='me-3 small'>{ formatCurrency( row.regular_price ) }</s><span className='text-success'>{ formatCurrency(row.price) }</span></> : <>{ formatCurrency(row.price) }</>, right: true, width:'150px'},
                { name: strings.cart_table_total, selector: row => row.itemTotal, format: row => formatCurrency(row.itemTotal), right: true, width:'150px'},
                { name: <i className='fa fa-trash'></i>, selector: row =>  row.id, format: row => row.freeItem ? '' : <button onClick={() => handleRemoveItemWithDiscount(row) } className="btn btn-primary text-white"><i className='fa fa-times'></i></button>, center: true, width: '100px'}
                
            ]}
            data={items}
            pagination
            paginationComponentOptions={paginationComponentOptions}
            paginationPerPage={ 10 }
            noTableHead={ props.noTableHead }
            striped={ true }
            theme="bs5"
            customStyles={ bs5Styles }
            noDataComponent={ <div className='alert alert-warning w-100 m-0'><i className="fa fa-exclamation-triangle me-3"></i>Aucune article dans le panier</div> }
        /> 
        { ! props.hideTotal && 
            <table className='table table-striped table-primary'>
                <tbody>
                    {
                        canApplyPromo
                        ? <tr className='d-flex'>
                            <td className='flex-grow-1 text-end'>
                            </td>
                            <td className='text-end'>
                                <div>
                                    { invalidPromoCode ? strings.invalid_code : '' }
                                    { nonEligibleProductsPromo ? strings.promo_products_non_eligible : ''}
                                    { appliedPromoCode ? strings.formatString(strings.promo_code_applied,{ code: appliedPromoCode }) : '' }
                                </div>
                                <div className='d-flex'>
                                    <input type="text" name="promo_text" className="form-control" onChange={ handlePromoInput } value={ promoCode } placeholder={ strings.enter_coupon } disabled={promoChecking} />
                                    <button type='button' className='btn btn-primary w-100 text-white' onClick={ checkPromoCode } disabled={promoChecking}>
                                        { strings.apply_coupon }
                                    </button>
                                </div>
                            </td>
                        </tr>
                        : ''
                    }
                    <tr className='d-flex'>
                        <td className='flex-grow-1 p-3 text-end'>
                            <strong>{ strings.total } :</strong>
                        </td>
                        <td className='p-3'>
                            { formatCurrency( cartTotal ) }
                        </td>
                    </tr>
                    <tr className='d-flex'>
                        <td className='flex-grow-1 p-3 text-end'>
                           
                        </td>
                        <td className='p-3'>
                            <Link className='btn btn-primary' to={orderPageUrl}><i className="fa fa-plus-circle me-3"></i>{ strings.nouvelle_commande }</Link>
                        </td>
                    </tr>
                </tbody>
            </table>
        } 
        </>     
    )
}

CartItems.defaultProps = {
    noTableHead: true,
  }
  

function MiniCart(){

    const {        
        totalItems
    } = useCart();
    
    return (
        <Link to="/cart"  className='cart text-end d-block text-white'>
            
            <span className='shopping-cart fs-1'>
                { totalItems }
                <i className='fa fa-shopping-bag mx-3'></i>
            </span>
        </Link>
    )

}


function QuantitySelector(props){

    const [ qty, setQty ] = useState(1)
    const [ toggled, setToggled ] = useState()
   
    useEffect(() => {
        const parsedQuantity = parseInt(props.quantity, 10);
        setQty(Number.isFinite(parsedQuantity) ? parsedQuantity : normalizePositiveInteger(props.packUnit))
        setToggled(props.toggled)
    },[props])
    

    const changeQty = (value) => {
        const packUnit = normalizePositiveInteger(props.packUnit);
        const minQty = props.min ? normalizePositiveInteger(props.min) : false;
        const maxQty = props.max ? normalizePositiveInteger(props.max) : false;
        let newQty = parseInt(value, 10);  

        if( ! newQty || newQty < 0)
        newQty = 0;

        const modulo = newQty % packUnit;

        
        newQty = newQty >= packUnit ? newQty - modulo : ( ! props.locked ? 0 : packUnit);
        
        if( (!minQty || newQty >= minQty) && (! maxQty || newQty <= maxQty) )
        {
            setQty(newQty)

            if(!newQty)
            setToggled(false)
    
            props.updateQuantity(newQty);

            if(props.collapseAfterChange && !props.locked) {
                setToggled(false);
                if(typeof props.onCollapse === "function")
                props.onCollapse();
            }
        }
       
    }

    const useOutsideAlerter = (ref, inputRef) => {
        useEffect(() => {
          /**
           * Alert if clicked on outside of element
           */
          function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                if(inputRef.current)
                changeQty(inputRef.current.value)
                setToggled(false)
            }
          }
          if(!props.locked)
          document.addEventListener("mousedown", handleClickOutside);

          return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
          };
        }, [ref,inputRef]);
    }

    const ref = useRef(null);
    const inputRef = useRef(null)
    
    useOutsideAlerter(ref,inputRef);

   

    const buttonClasses = "btn btn-primary d-block text-white" + ( props.layout === "horizontal" ? ''  : " w-100" )
    const InputWrapperClasses = props.layout === "horizontal" ? "flex-grow-1" : ""

    

    
    return (

        <div ref={ ref } className={ 'cart-qty' + ((toggled || props.locked) ? ' is-expanded' : '') }>
            { ! toggled && ! props.locked
            ?
            <button type="button" onClick={ () => setToggled(true) } className="btn btn-primary d-block text-white w-100">
                { qty  }
            </button>
            :
            <div className={ (props.layout === "horizontal" ? "d-flex " : '') + "cart-update-qty"} onClick={ e => e.stopPropagation() }>
                <Repeatable 
				repeatDelay={0}
                repeatInterval={450}
				onHold={ () => changeQty(normalizeNonNegativeInteger(qty, 0) - normalizePositiveInteger(props.packUnit)) }
				
				>
                    <button type='button' className={ buttonClasses } >-</button>
                </Repeatable>
                <div className={ InputWrapperClasses }>
                    <input ref={ inputRef } type="text" className="bg-white border-1 border-primary text-primary border-0 p-3 fw-bold text-center w-100" onChange={ event => { setQty(event.target.value) }} onBlur={ (event) =>  changeQty(event.target.value) } value={ qty }  />
                </div>
                <Repeatable 
				repeatDelay={0}
                repeatInterval={450}
				onHold={ () => changeQty(normalizeNonNegativeInteger(qty, 0) + normalizePositiveInteger(props.packUnit)) }
				
				>
                    <button type='button' className={ buttonClasses } >+</button>
                </Repeatable>
                
            </div>
            }
        </div>
    )
}

QuantitySelector.defaultProps = {
    locked: false,
    toggled: false,
    collapseAfterChange: false,
    packUnit: 1,
    min: false,
    max:false,
  }
  
export { MiniCart, QuantitySelector, CartItems, formatCurrency, getDiscountPrice, isDiscounted, getClientGeneralDiscount, getFinalDiscountPercentage, getFinalDiscountPrice, getAppliedDiscountType, getFreeProductMinQty, getFreeProductBaseData };